import { useQuery } from '@tanstack/react-query';
import { authFetch } from '@/lib/authFetch';
import { useAuth } from '@/hooks/useAuth';

export interface RewardTokenBreakdown {
  tokenId: string;
  outcomeName?: string;
  price?: number;
}

export interface MarketRewardBreakdown {
  conditionId: string;
  marketSlug?: string;
  question?: string;
  eventSlug?: string;
  image?: string;
  liquidityRewardUsd: number;
  makerRebateUsd: number;
  tokens: RewardTokenBreakdown[];
  earningPercentage?: number;
  rewardsMaxSpread?: number;
  rewardsMinSize?: number;
}

export interface RewardsSnapshot {
  makerAddress: string;
  date: string;
  totalLiquidityRewardUsd: number;
  totalMakerRebateUsd: number;
  /** All markets the user is participating in today (may include $0 rows). */
  byMarket: MarketRewardBreakdown[];
  /** Number of markets in byMarket with earnings > 0. Use for UI counters. */
  earningMarketsCount: number;
  fetchedAt: number;
}

export interface RewardsSummary {
  range: 'today' | '7d' | '30d' | 'lifetime';
  totalLiquidityRewardUsd: number;
  totalMakerRebateUsd: number;
  daily: Array<{ date: string; liquidityRewardUsd: number; makerRebateUsd: number }>;
}

// All rewards queries are scoped to the current user. We include `userId` in
// the queryKey so switching accounts on the same browser evicts the prior
// user's cached totals instead of flashing them to the new user during the
// revalidate window. Anonymous users (userId=null) still get a distinct key
// so the cache doesn't collide with any logged-in session.

export function useRewardsToday(enabled: boolean = true) {
  const { userId } = useAuth();
  return useQuery<RewardsSnapshot>({
    queryKey: ['polymarket-rewards', 'today', userId],
    queryFn: async () => {
      const r = await authFetch('/api/polymarket-rewards/today');
      if (!r.ok) throw new Error(`rewards today ${r.status}`);
      return r.json();
    },
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useRewardsSummary(range: RewardsSummary['range'], enabled: boolean = true) {
  const { userId } = useAuth();
  return useQuery<RewardsSummary>({
    queryKey: ['polymarket-rewards', 'summary', userId, range],
    queryFn: async () => {
      const r = await authFetch(`/api/polymarket-rewards/summary?range=${range}`);
      if (!r.ok) throw new Error(`rewards summary ${r.status}`);
      return r.json();
    },
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Live per-order scoring status. Polls CLOB every 60s via our backend (L2
 * auth required). Enables a "⚡ Earning" badge on the user's resting orders.
 */
export function useOrdersScoring(orderIds: string[], enabled: boolean = true) {
  const { userId } = useAuth();
  return useQuery<{ scoring: Record<string, boolean> }>({
    queryKey: ['polymarket-rewards', 'orders-scoring', userId, [...orderIds].sort().join(',')],
    queryFn: async () => {
      if (orderIds.length === 0) return { scoring: {} };
      const r = await authFetch('/api/polymarket-rewards/orders-scoring', {
        method: 'POST',
        body: JSON.stringify({ orderIds }),
      });
      if (!r.ok) throw new Error(`orders-scoring ${r.status}`);
      return r.json();
    },
    enabled: enabled && !!userId && orderIds.length > 0,
    staleTime: 45 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useRewardsForMarket(conditionId: string | undefined, enabled: boolean = true) {
  const { userId } = useAuth();
  // UTC day key — a tab left open across midnight UTC would otherwise keep
  // serving yesterday's snapshot until staleTime lapses. The backend always
  // serves "today" implicitly, so rotating the key at the day boundary
  // forces a fresh fetch for the new day's data.
  const utcDay = new Date().toISOString().slice(0, 10);
  return useQuery<{ conditionId: string; found: boolean; breakdown: MarketRewardBreakdown | null }>({
    queryKey: ['polymarket-rewards', 'by-market', userId, conditionId, utcDay],
    queryFn: async () => {
      const r = await authFetch(`/api/polymarket-rewards/by-market/${conditionId}`);
      if (!r.ok) throw new Error(`rewards by-market ${r.status}`);
      return r.json();
    },
    enabled: enabled && !!userId && !!conditionId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}
